import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Navbar } from '../components';
import { paymentsAPI, authAPI, avatarsAPI, plansAPI } from '../services/api';
import {
  resolveActivePlanRoute,
  canAccessInviteeChat,
  shouldRouteUnpaidSendInviteToPayment,
  filterGroupChatUpgradePlans,
  resolveInviteePostAuthRoute,
  fetchActiveSubscriptionFromApi,
  isSubscriptionActive,
} from '../utils/freeTrial';
import './PlanSelectionPage.css';

const FALLBACK_PLANS = [
  {
    id: 20,
    interval: 'monthly',
    label: 'Monthly',
    priceUsd: 19.99,
    price: '$19.99',
    period: '/month',
    badge: null,
    tagline: 'Full access, billed monthly.',
  },
  {
    id: 21,
    interval: 'yearly',
    label: 'Annual',
    priceUsd: 149.99,
    price: '$149.99',
    period: '/year',
    badge: 'Best Value',
    tagline: 'Save over 37% — billed once a year.',
  },
];

function formatPlanPrice(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '$0.00';
  return `$${value.toFixed(2)}`;
}

function floorHalfMonthlyPrice(priceUsd) {
  const cents = Math.floor(Number(priceUsd) * 100 * 0.5);
  return cents / 100;
}

function discountedPlanPrice(plan, { firstMonthDiscount, isGroupJoinFlow }) {
  if (isGroupJoinFlow || !firstMonthDiscount) return plan.price;
  const priceUsd = Number(plan.priceUsd);
  if (!Number.isFinite(priceUsd) || priceUsd <= 0) return plan.price;
  if (plan.interval === 'monthly') {
    return formatPlanPrice(floorHalfMonthlyPrice(priceUsd));
  }
  if (plan.interval === 'yearly') {
    return formatPlanPrice(Math.max(0, priceUsd - 30));
  }
  return plan.price;
}

function buildPlanCardsFromAvatarPlans(rawAvatarPlans, { groupOnly = false } = {}) {
  if (!Array.isArray(rawAvatarPlans) || rawAvatarPlans.length === 0) return [];

  const filteredPlans = rawAvatarPlans.filter((plan) => {
    const typeName = String(plan.avatar_type_name || '');
    const isGroupType = /group/i.test(typeName);
    return groupOnly ? isGroupType : !isGroupType;
  });

  const cards = [];
  for (const plan of filteredPlans) {
    const interval = String(plan.interval || '').toLowerCase();
    const priceUsd = Number(plan.price_usd ?? plan.price ?? 0);
    if (!Number.isFinite(priceUsd) || priceUsd <= 0) continue;

    if (interval === 'monthly') {
      cards.push({
        id: plan.id,
        interval: 'monthly',
        label: 'Monthly',
        priceUsd,
        price: formatPlanPrice(priceUsd),
        period: '/month',
        badge: null,
        tagline: 'Full access, billed monthly.',
      });
    } else if (interval === 'yearly' || interval === 'year') {
      cards.push({
        id: plan.id,
        interval: 'yearly',
        label: 'Annual',
        priceUsd,
        price: formatPlanPrice(priceUsd),
        period: '/year',
        badge: 'Best Value',
        tagline: 'Save over 37% — billed once a year.',
      });
    }
  }

  const intervalOrder = { monthly: 0, yearly: 1, year: 1 };
  cards.sort((a, b) => (intervalOrder[a.interval] ?? 9) - (intervalOrder[b.interval] ?? 9));
  return cards;
}

const GROUP_JOIN_FEATURES = [
  'Chat with your family\'s shared Profile',
  'Join group conversations with invited members',
  'Add memories and documents to the shared Profile',
  'Secure, private storage for everything you share',
];

const OWNER_FEATURES = [
  'Invite friends and family to connect with the Avatar you create',
  // 'Create multiple Avatars within one Family Group',
  'Add new documents and memories at any time',
  'Secure, private storage for everything you share',
];

function groupAvatarPlansFromAddons(response) {
  const resultData = response?.data || response;
  const rawAvatarPlans = resultData?.avatar_plans || [];
  if (!Array.isArray(rawAvatarPlans) || rawAvatarPlans.length === 0) return [];

  const plansByType = {};
  rawAvatarPlans.forEach((plan) => {
    const typeName = plan.avatar_type_name || 'Generic';
    if (!plansByType[typeName]) {
      plansByType[typeName] = {
        id: plan.avatar_type_id || Math.random(),
        name: typeName,
        plans: [],
      };
    }

    const normalizedPlan = { ...plan };
    const planInterval = (plan.interval || '').toLowerCase();
    const planPrice = Number(plan.price_usd || plan.price || 0);

    if (planInterval === 'yearly' || planInterval === 'year') {
      normalizedPlan.yearly_price_usd = planPrice;
      normalizedPlan.monthly_price_usd = null;
    } else {
      normalizedPlan.monthly_price_usd = planPrice;
      normalizedPlan.yearly_price_usd = null;
    }

    plansByType[typeName].plans.push(normalizedPlan);
  });

  return Object.values(plansByType);
}

async function loadSelectablePlans({ groupOnly = false } = {}) {
  const addonsRes = await plansAPI.getAddons();
  const resultData = addonsRes?.data || addonsRes;
  const rawAvatarPlans = resultData?.avatar_plans || [];

  if (groupOnly) {
    const avatarTypes = groupAvatarPlansFromAddons(addonsRes);
    return flattenGroupJoinPlans(filterGroupChatUpgradePlans(avatarTypes));
  }

  return buildPlanCardsFromAvatarPlans(rawAvatarPlans, { groupOnly: false });
}

function flattenGroupJoinPlans(avatarPlanTypes) {
  const types = filterGroupChatUpgradePlans(avatarPlanTypes);
  const cards = [];

  for (const type of types) {
    for (const plan of type.plans || []) {
      const monthly = Number(plan.monthly_price_usd);
      const yearly = Number(plan.yearly_price_usd);

      if (Number.isFinite(monthly) && monthly > 0) {
        cards.push({
          id: plan.id,
          interval: 'monthly',
          label: 'Monthly',
          priceUsd: monthly,
          price: formatPlanPrice(monthly),
          period: '/month',
          badge: null,
          tagline: '',
        });
      }
      if (Number.isFinite(yearly) && yearly > 0) {
        cards.push({
          id: plan.id,
          interval: 'yearly',
          label: 'Annual',
          priceUsd: yearly,
          price: formatPlanPrice(yearly),
          period: '/year',
          badge: 'Best Value',
          tagline: '',
        });
      }
    }
  }

  const intervalOrder = { monthly: 0, yearly: 1, year: 1 };
  cards.sort((a, b) => (intervalOrder[a.interval] ?? 9) - (intervalOrder[b.interval] ?? 9));

  return cards;
}

const PlanSelectionPage = () => {
  const navigate = useNavigate();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  const [isGroupJoinFlow, setIsGroupJoinFlow] = useState(false);
  const [displayPlans, setDisplayPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const cleanupRef = useRef(null);

  const navigateAfterInvitePayment = async () => {
    try {
      sessionStorage.removeItem('user_fetched');
      sessionStorage.removeItem('user_data');
    } catch (_) { /* ignore */ }

    let meData = null;
    try {
      const meRes = await authAPI.getCurrentUser();
      meData = meRes?.data || meRes;
      const apiSub = await fetchActiveSubscriptionFromApi(localStorage.getItem('access_token'));
      if (isSubscriptionActive(apiSub)) {
        meData = { ...(meData || {}), subscription: apiSub };
      }
    } catch (_) { /* ignore */ }

    const uid = meData?.id || meData?.user_id;
    const route = await resolveInviteePostAuthRoute({
      userData: meData,
      userId: uid,
      avatarsAPI,
    });
    navigate(route || '/chat', { replace: true });
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/signin', { replace: true });
      return;
    }

    const checkSub = async () => {
      let meData = null;
      try {
        try {
          const meRes = await authAPI.getCurrentUser();
          meData = meRes?.data || meRes;
          const apiSub = await fetchActiveSubscriptionFromApi(token);
          const meWithSub = isSubscriptionActive(apiSub)
            ? { ...(meData || {}), subscription: apiSub }
            : meData;

          if (canAccessInviteeChat({ userData: meWithSub })) {
            const uid = meWithSub?.id || meWithSub?.user_id;
            const route = await resolveInviteePostAuthRoute({
              userData: meWithSub,
              userId: uid,
              avatarsAPI,
            });
            navigate(route || '/chat', { replace: true });
            return;
          }

          if (shouldRouteUnpaidSendInviteToPayment({ userData: meWithSub })) {
            setIsGroupJoinFlow(true);
            setLoadingPlans(true);
            try {
              const groupPlans = await loadSelectablePlans({ groupOnly: true });
              if (groupPlans.length > 0) {
                setDisplayPlans(groupPlans);
              } else {
                setDisplayPlans([]);
                toast.error('No group chat plans are available right now.');
              }
            } catch (err) {
              console.error('Failed to load group chat plans:', err);
              toast.error('Could not load group chat plans. Please refresh and try again.');
              setDisplayPlans([]);
            } finally {
              setLoadingPlans(false);
            }
            if (sessionStorage.getItem('first_month_discount_active') === 'true') {
              setFirstMonthDiscount(true);
            }
            setCheckingSubscription(false);
            return;
          }
        } catch (_) { /* continue to subscription check */ }

        const apiBase = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
        const res = await fetch(`${apiBase}/api/addons/my-plan`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          let userId = null;
          try {
            if (!meData) {
              const me = await authAPI.getCurrentUser();
              meData = me?.data || me;
            }
            userId = meData?.id || meData?.user_id;
          } catch (_) { /* ignore */ }
          const route = await resolveActivePlanRoute({
            token,
            userId,
            avatarsAPI,
            userData: meData,
          });
          navigate(route, { replace: true });
          return;
        }
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('access_token');
          navigate('/signin', { replace: true });
          return;
        }
      } catch (_) { /* show plan selection */ }

      if (sessionStorage.getItem('first_month_discount_active') === 'true') {
        setFirstMonthDiscount(true);
      }

      setLoadingPlans(true);
      try {
        const ownerPlans = await loadSelectablePlans({ groupOnly: false });
        if (ownerPlans.length > 0) {
          setDisplayPlans(ownerPlans);
        } else {
          console.warn('No active owner plans returned from API; using fallback prices.');
          setDisplayPlans(FALLBACK_PLANS);
          toast.warn('Could not load current plan prices. Showing default prices.');
        }
      } catch (err) {
        console.error('Failed to load plan prices:', err);
        setDisplayPlans(FALLBACK_PLANS);
        toast.error('Could not load plan prices. Please refresh and try again.');
      } finally {
        setLoadingPlans(false);
      }

      setCheckingSubscription(false);
    };
    checkSub();
  }, [navigate]);

  useEffect(() => {
    const onPopState = () => {
      window.history.pushState(null, '', window.location.pathname);
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  const handleSelectPlan = async (plan) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/signin', { replace: true });
      return;
    }

    setLoadingPlanId(plan.id);
    const isMonthlyDiscounted = !isGroupJoinFlow && firstMonthDiscount && plan.interval === 'monthly';
    const isAnnualDiscounted = !isGroupJoinFlow && firstMonthDiscount && plan.interval === 'yearly';

    const popupWidth = 500;
    const popupHeight = 700;
    const left = Math.round(window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - popupHeight) / 2);
    const popup = window.open(
      'about:blank',
      'PayPal',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
    const popupBlocked = !popup || popup.closed || typeof popup.closed === 'undefined';

    try {
      const res = await paymentsAPI.createPaypalOrder({
        avatar_plan_id: plan.id,
        plan_interval: plan.interval,
        first_month_discount: isMonthlyDiscounted,
        annual_discount: isAnnualDiscounted,
      });

      const data = res?.data || res;
      const approveUrl = data?.approve_url;
      const orderId = data?.order_id || null;
      if (!approveUrl) {
        if (!popupBlocked) popup.close();
        toast.error('Could not start payment. Please try again.');
        setLoadingPlanId(null);
        return;
      }

      const storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
      const isInvitee = !!(storedInvite.avatar_id || storedInvite.invite_id);
      const started_from = isInvitee ? '/select-plan' : '/create-avatar';
      const ctx = {
        order_id: orderId,
        avatar_plan_id: plan.id,
        plan_interval: plan.interval,
        auto_renew: true,
        started_from,
        context: 'popup',
        first_month_discount: isMonthlyDiscounted,
        annual_discount: isAnnualDiscounted,
      };
      sessionStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));
      localStorage.setItem('paypal_checkout_context', JSON.stringify(ctx));

      if (popupBlocked || popup.closed) {
        window.location.href = approveUrl;
        return;
      }

      popup.location.href = approveUrl;
      setPopupOpen(true);

      const messageHandler = (event) => {
        if (event.data?.type === 'PAYPAL_SUCCESS') {
          if (cleanupRef.current) cleanupRef.current();
          if (isInvitee) {
            navigateAfterInvitePayment();
          } else {
            try {
              sessionStorage.removeItem('user_fetched');
              sessionStorage.removeItem('user_data');
            } catch (_) { /* ignore */ }
            navigate('/create-avatar', { replace: true });
          }
        } else if (event.data?.type === 'PAYPAL_ERROR') {
          if (cleanupRef.current) cleanupRef.current();
          toast.error('Payment failed. Please try again.');
          setLoadingPlanId(null);
          setPopupOpen(false);
        }
      };

      const pollTimer = setInterval(() => {
        if (popup.closed) {
          if (cleanupRef.current) cleanupRef.current();
          setLoadingPlanId(null);
          setPopupOpen(false);
        }
      }, 600);

      cleanupRef.current = () => {
        clearInterval(pollTimer);
        window.removeEventListener('message', messageHandler);
        cleanupRef.current = null;
      };

      window.addEventListener('message', messageHandler);
    } catch (err) {
      if (!popupBlocked) popup.close();
      console.error('PayPal create order failed:', err);
      if (err?.status === 401 || err?.statusCode === 401) {
        localStorage.removeItem('access_token');
        navigate('/signin', { replace: true });
        return;
      }
      toast.error(err?.message || 'Payment initiation failed. Please try again.');
      setLoadingPlanId(null);
    }
  };

  if (checkingSubscription) {
    return (
      <div className="plan-selection-page">
        <Navbar disabled={true} variant="transparent" />
        <div className="plan-selection-loading">
          <div className="spinner-border-sm" />
        </div>
      </div>
    );
  }

  const featureList = isGroupJoinFlow ? GROUP_JOIN_FEATURES : OWNER_FEATURES;

  return (
    <div className="plan-selection-page">
      <Navbar disabled={true} variant="transparent" />

      <main className="plan-selection-main">
        <div className="plan-selection-container">
          {isGroupJoinFlow && (
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 600 }}>
                Join your family group
              </h2>
              <p style={{ margin: 0, color: 'var(--slate)', fontSize: '15px' }}>
                Choose a plan to access your family&apos;s shared Profile and group chat.
              </p>
            </div>
          )}

          {loadingPlans ? (
            <div className="plan-selection-loading">
              <div className="spinner-border-sm" />
              <p style={{ marginTop: '12px', color: 'var(--slate)' }}>Loading plans...</p>
            </div>
          ) : displayPlans.length === 0 ? (
            <div className="plan-selection-loading">
              <p style={{ color: 'var(--slate)' }}>No plans are available right now. Please try again later.</p>
            </div>
          ) : (
            <div className="plan-cards">
              {displayPlans.map((plan) => {
                const showDiscount = !isGroupJoinFlow && firstMonthDiscount
                  && (plan.interval === 'monthly' || plan.interval === 'yearly');
                const displayPrice = discountedPlanPrice(plan, { firstMonthDiscount, isGroupJoinFlow });

                return (
                <div
                  key={`${plan.id}-${plan.interval}`}
                  className={`plan-card${plan.badge ? ' plan-card--featured' : ''}`}
                >
                  {plan.badge && (
                    <div className="plan-card__badge">{plan.badge}</div>
                  )}
                  <div className="plan-card__label">{plan.label}</div>
                  <div className="plan-card__price">
                    {showDiscount && (
                      <span className="plan-card__price-original">{plan.price}</span>
                    )}
                    <span className="plan-card__price-amount">
                      {displayPrice}
                    </span>
                    <span className="plan-card__price-period">{plan.period}</span>
                  </div>
                  {/* {!isGroupJoinFlow && firstMonthDiscount && plan.interval === 'monthly' ? (
                    <p className="plan-card__tagline plan-card__tagline--discount">your first month</p>
                  ) : !isGroupJoinFlow && firstMonthDiscount && plan.interval === 'yearly' ? (
                    <p className="plan-card__tagline plan-card__tagline--discount">Save 50% vs monthly — billed once a year</p>
                  ) : (
                    <p className="plan-card__tagline">{plan.tagline}</p>
                  )} */}

                  <button
                    className="plan-card__cta"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={loadingPlanId !== null}
                  >
                    {loadingPlanId === plan.id
                      ? (popupOpen ? 'Complete payment in popup…' : 'Starting…')
                      : (isGroupJoinFlow ? 'Join Group' : 'Get Started')}
                  </button>
                </div>
                );
              })}
            </div>
          )}

          <ul className="plan-shared-features">
            {featureList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          {popupOpen && (
            <p className="plan-selection-popup-hint">
              A PayPal window has opened — complete your payment there to continue.
            </p>
          )}

          <p className="plan-selection-footer">
            Payments are processed securely via PayPal. You can cancel anytime.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PlanSelectionPage;
